import React, { useState, useEffect } from 'react';
import { RefreshCw, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const WANOverview = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [syncInfo, setSyncInfo] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch overview stats
      const response = await fetch(`${API_URL}/api/wan/overview-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }

      // Fetch sync info
      const syncResponse = await fetch(`${API_URL}/api/wan/sync/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (syncResponse.ok) {
        const syncData = await syncResponse.json();
        setSyncInfo(syncData.data);
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
      toast.error('Failed to load overview data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
    // No polling - WAN overview is refreshed on-demand by user
  }, []);

  const COLORS = {
    online: '#10b981',
    offline: '#ef4444',
    offline_7d: '#f59e0b',
    offline_30d: '#6b7280',
    good: '#10b981',
    fair: '#fbbf24',
    critical: '#ef4444',
    huawei: '#22d3ee',
    fiberhome: '#84cc16',
    zte: '#fbbf24',
    mikrotik: '#f472b6',
    other: '#f97316',
    gpon: '#06b6d4',
    epon: '#22c55e',
    ethernet: '#a855f7',
    temp_cool: '#3b82f6',
    temp_warm: '#fbbf24',
    temp_hot: '#f97316',
    temp_critical: '#ef4444'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-2 dark:text-blue-400" />
          <p className="text-slate-600 dark:text-slate-300">Loading overview...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/30">
          <p className="text-red-600 dark:text-red-400">Failed to load overview data</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border rounded shadow-lg p-2 dark:bg-slate-800">
          <p className="font-medium">{payload[0].name}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {payload[0].value} ({((payload[0].value / payload[0].payload.total) * 100).toFixed(2)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderPieChart = (data, colors, title) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const dataWithTotal = data.map(item => ({ ...item, total }));

    return (
      <div className="bg-white p-4 rounded-md border border-slate-200 hover:border-slate-300 transition-colors dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-600">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 dark:text-slate-200">{title}</h3>
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={dataWithTotal}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={70}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[entry.color] || '#94a3b8'} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              layout="vertical" 
              align="right" 
              verticalAlign="middle"
              iconSize={10}
              formatter={(value, entry) => (
                <span className="text-xs">
                  {value}: <b>{entry.payload.value}</b> ({((entry.payload.value / total) * 100).toFixed(1)}%)
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
        <p className="text-center text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100 dark:text-slate-400 dark:border-slate-700">
          Total: <span className="font-semibold text-slate-700 dark:text-slate-200">{total}</span>
        </p>
      </div>
    );
  };

  // Prepare data for charts
  const statusData = [
    { name: 'Online', value: stats.status.online, color: 'online' },
    { name: 'Offline < 7d', value: stats.status.offline_recent, color: 'offline' },
    { name: 'Offline > 7d', value: stats.status.offline_7d, color: 'offline_7d' },
    { name: 'Offline > 30d', value: stats.status.offline_30d, color: 'offline_30d' }
  ];

  const accessTypeData = [
    { name: 'GPON', value: stats.access_type.gpon, color: 'gpon' },
    { name: 'EPON', value: stats.access_type.epon, color: 'epon' },
    { name: 'Ethernet', value: stats.access_type.ethernet, color: 'ethernet' },
    { name: 'Unknown', value: stats.access_type.unknown, color: 'offline_30d' }
  ];

  const manufacturerData = [
    { name: 'Huawei', value: stats.manufacturer.huawei, color: 'huawei' },
    { name: 'FiberHome', value: stats.manufacturer.fiberhome, color: 'fiberhome' },
    { name: 'ZTE', value: stats.manufacturer.zte, color: 'zte' },
    { name: 'MikroTik', value: stats.manufacturer.mikrotik, color: 'mikrotik' },
    { name: 'Other', value: stats.manufacturer.other, color: 'other' }
  ];

  const signalQualityData = [
    { name: 'Good', value: stats.signal_quality.good, color: 'good' },
    { name: 'Fair', value: stats.signal_quality.fair, color: 'fair' },
    { name: 'Critical', value: stats.signal_quality.critical, color: 'critical' },
    { name: 'Unknown', value: stats.signal_quality.unknown, color: 'offline_30d' }
  ];

  const temperatureData = [
    { name: 'Cool (< 45°C)', value: stats.temperature.cool, color: 'temp_cool' },
    { name: 'Warm (45-65°C)', value: stats.temperature.warm, color: 'temp_warm' },
    { name: 'Hot (65-70°C)', value: stats.temperature.hot, color: 'temp_hot' },
    { name: 'Critical (> 70°C)', value: stats.temperature.critical, color: 'temp_critical' }
  ];

  const registrationData = [
    { name: 'Today', value: stats.registration.today, color: 'good' },
    { name: '< 7 days', value: stats.registration.week, color: 'fair' },
    { name: '< 30 days', value: stats.registration.month, color: 'offline_7d' },
    { name: '> 30 days', value: stats.registration.older, color: 'offline_30d' }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header - Compact */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <Activity className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            WAN Devices Overview
          </h1>
          <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
            Real-time monitoring dashboard • TR-069 Management
            {syncInfo?.settings?.enabled && (
              <span className="ml-2 text-xs text-green-600 font-medium dark:text-green-400">
                • Auto-sync: Every {syncInfo.settings.interval_minutes}min
              </span>
            )}
          </p>
        </div>
        <Button onClick={fetchOverviewData} size="sm" variant="outline" className="text-xs">
          <RefreshCw className="w-3 h-3 mr-1.5" />
          Refresh
        </Button>
      </div>

      {/* Last Sync Info - Compact */}
      {syncInfo?.last_sync && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 dark:bg-blue-900/30">
          <p className="text-xs text-blue-900">
            <span className="font-semibold">Last Sync:</span> {new Date(syncInfo.last_sync.timestamp).toLocaleString('id-ID')} 
            {syncInfo.last_sync.status === 'success' && (
              <span className="ml-2 text-green-700 font-medium dark:text-green-300">
                ✓ {syncInfo.last_sync.new} new, {syncInfo.last_sync.updated} updated
              </span>
            )}
          </p>
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {renderPieChart(statusData, COLORS, 'Device Status')}
        {renderPieChart(accessTypeData, COLORS, 'Access Type')}
        {renderPieChart(signalQualityData, COLORS, 'Signal Quality (Optical RX)')}
        {renderPieChart(manufacturerData, COLORS, 'Device Manufacturer')}
        {renderPieChart(registrationData, COLORS, 'Device Registration')}
        {renderPieChart(temperatureData, COLORS, 'Optical Temperature')}
      </div>

      {/* Critical Alerts - Compact */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {stats.signal_quality.critical > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 dark:bg-red-900/30">
            <p className="text-red-800 font-semibold text-sm flex items-center gap-1">
              <span className="text-base">⚠️</span> Critical Signal Quality
            </p>
            <p className="text-red-700 text-xs mt-1 dark:text-red-300">
              {stats.signal_quality.critical} devices require immediate attention
            </p>
          </div>
        )}
        
        {stats.temperature.critical > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 dark:bg-red-900/30">
            <p className="text-red-800 font-semibold text-sm flex items-center gap-1">
              <span className="text-base">🌡️</span> Critical Temperature
            </p>
            <p className="text-red-700 text-xs mt-1 dark:text-red-300">
              {stats.temperature.critical} devices above 70°C
            </p>
          </div>
        )}
        
        {stats.status.offline_30d > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 dark:bg-amber-900/30">
            <p className="text-amber-800 font-semibold text-sm flex items-center gap-1">
              <span className="text-base">📴</span> Long Offline Devices
            </p>
            <p className="text-amber-700 text-xs mt-1 dark:text-amber-300">
              {stats.status.offline_30d} devices offline > 30 days
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WANOverview;
